<template>
  <div class="flex flex-col min-h-full">
    <!-- Back button (mobile only) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="handleBack"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>
    <!-- Back button (desktop) -->
    <div v-if="!isMobileDevice" class="absolute flex items-center gap-2 px-4 pt-4">
      <IconButton
        variant="text"
        size="sm"
        aria-label="Go back"
        @click="handleBack"
      >
        <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
      </IconButton>
    </div>
    <div class="flex-1 flex items-center justify-center px-10">
    <div class="bg-surface rounded-2xl px-6 py-5 w-full max-w-2xl relative overflow-hidden text-text-primary">
      <!-- Background decoration -->
      <div class="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div class="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <!-- Loading state -->
      <div v-if="isLoadingCurrentUser" class="flex items-center justify-center py-8">
        <div class="w-8 h-8 border-4 border-t-primary border-surface rounded-full animate-spin"></div>
      </div>

      <!-- Content (hero left, form right) -->
      <div v-else class="relative z-10 grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6 items-center">
        <!-- Hero column -->
        <div class="flex flex-col justify-center">
          <h2 class="text-2xl sm:text-3xl font-bold leading-tight">Play Together</h2>
          <p class="text-text-secondary mt-2 text-sm sm:text-base">
            <span v-if="isTeacher && !isAdmin">Create a new classroom session</span>
            <span v-else-if="isStudent">Join a classroom session</span>
            <span v-else>Create or join a classroom session</span>
          </p>
        </div>

        <!-- Form column -->
        <div class="flex flex-col min-w-0">
          <UTabs
            v-if="isAdmin"
            v-model="activeTab"
            :items="[{ label: 'Create', value: 'create' }, { label: 'Join', value: 'join' }]"
            class="mb-3"
            :ui="{
              trigger: 'data-[state=active]:text-text-primary'
            }"
          />

          <div :class="{ 'h-[220px]': isAdmin }">
            <ClassroomCreateForm
              v-if="isTeacher || (isAdmin && activeTab === 'create')"
              @created="handleRoomCreated"
            />

            <ClassroomJoinForm
              v-if="isStudent || (isAdmin && activeTab === 'join')"
              @joined="handleRoomJoined"
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { goBackByRoute } from '@/composables/useRouteBack';
import ClassroomCreateForm from '@/components/classroom/ClassroomCreateForm.vue';
import ClassroomJoinForm from '@/components/classroom/ClassroomJoinForm.vue';
import IconButton from '@/components/IconButton.vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';

const route = useRoute();
const router = useRouter();
const { isMobileDevice } = useDeviceDetection();
const activeTab = ref<'create' | 'join'>('create');

const { data: currentUser, isPending: isLoadingCurrentUser } = useConvexQuery(
  api.users.getCurrentUser,
  {}
);

const isTeacher = computed(() => currentUser.value?.role === 'teacher');
const isStudent = computed(() => currentUser.value?.role === 'student');
const isAdmin = computed(() => currentUser.value?.role === 'admin');

const handleRoomCreated = (roomCode: string) => {
  router.push({ name: 'classroom-lobby', params: { code: roomCode } });
};

const handleRoomJoined = (roomCode: string, roomStatus?: string) => {
  if (roomStatus === 'ready') {
    router.push({ name: 'classroom-game', params: { code: roomCode } });
    return;
  }
  router.push({ name: 'classroom-lobby', params: { code: roomCode } });
};

const handleBack = () => {
  goBackByRoute(router, route);
};
</script>
